import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    CardPointeIpgSystemId,
    CardPointeIpgSystemIdKey,
    CardPointeGatewayEnvironment,
    defaultDemoIpgConfigByKey,
    defaultIpgConfigByKey,
    type CardPointeIpgConfig,
    type IpgConfig,
    type MerchantConfig,
    PaymentActionAvailabilityStatus,
    type PaymentActionAvailability,
    type PaymentActionReadinessResponse,
    type TerminalDeviceListItem,
} from '@anchorbase/pillar-sdk';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { paymentsApi, paymentsInfrastructureApi } from '@/lib/pillarApi';
import {
    formatIpgPaymentFeatureCapabilityStateLabel,
    IpgPaymentFeatureCapabilityState,
    type IpgConfigEditorAdapter,
    type IpgPaymentFeatureCapabilityDisplayRule,
} from '../../ipg-config-editor-adapter';

export type CardPointeOrganizationIpgEnvironment =
    | 'PRODUCTION'
    | 'DEMO'
    | 'CUSTOM';

export interface CardPointeOrganizationIpgConfigEditorState
{
    readonly ipgSystemId: typeof CardPointeIpgSystemId;
    readonly ipgSystemIdKey: typeof CardPointeIpgSystemIdKey;
    readonly environment: CardPointeOrganizationIpgEnvironment;
    readonly gatewayEnvironment: CardPointeGatewayEnvironment;
    readonly portalUrl: string;
}

export interface CardPointeOrganizationIpgConfigEditorProps {
    readonly editorState: CardPointeOrganizationIpgConfigEditorState;
    readonly onChange: (
        nextState: CardPointeOrganizationIpgConfigEditorState
    ) => void;
    readonly onPresetConfigChange: (ipgConfig: IpgConfig) => void;
    readonly onPortalUrlChange: (portalUrl: string) => void;
}

export interface CardPointeOrganizationIpgReadOnlySummaryProps {
    readonly editorState: CardPointeOrganizationIpgConfigEditorState;
}

export interface CardPointeOrganizationIpgReadinessSummaryProps {
    readonly organizationUuid: string;
}

const CARDPOINTE_READINESS_TERMINAL_PAGE_SIZE = 50;

const CardPointeReadinessActionDefinitions = [
    { key: 'cardEntry', label: 'Card entry' },
    { key: 'storedCard', label: 'Stored card' },
    { key: 'terminalSale', label: 'Terminal sale' },
] as const satisfies ReadonlyArray<{
    readonly key: keyof PaymentActionReadinessResponse;
    readonly label: string;
}>;

export const CardPointeOrganizationIpgCapabilityDisplayRules: readonly IpgPaymentFeatureCapabilityDisplayRule[] =
    [
        {
            featureId: 'browser_tokenization',
            label: 'Browser tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'CardSecure browser tokenization can create payment tokens without storing PAN in the app.',
        },
        {
            featureId: 'hosted_tokenization',
            label: 'Hosted tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe Hosted iFrame tokenization is available.',
        },
        {
            featureId: 'terminal_payments',
            label: 'Terminal payments',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe terminal payment APIs are configured.',
        },
        {
            featureId: 'terminal_tokenization',
            label: 'Terminal tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'CardPointe can tokenize cards from a terminal for stored-card payments.',
        },
        {
            featureId: 'heartbeat',
            label: 'Heartbeat',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe merchant inquiry can verify gateway readiness.',
        },
        {
            featureId: 'refunds',
            label: 'Refunds',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe refund APIs are configured.',
        },
        {
            featureId: 'voids',
            label: 'Voids',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe void APIs are configured.',
        },
        {
            featureId: 'ach',
            label: 'ACH payments',
            state: IpgPaymentFeatureCapabilityState.DEFERRED,
            tooltip:
                'ACH remains disabled until the CardPointe ACH guide is implemented and QA is complete.',
        },
    ];

function cloneCardPointeOrganizationIpgConfig(
    config: CardPointeIpgConfig
): CardPointeIpgConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map((fetchPolicy) => ({
                action: fetchPolicy.action,
                urlPatterns: [...fetchPolicy.urlPatterns],
            })),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map((iframePolicy) => ({
                action: iframePolicy.action,
                urlPatterns: [...iframePolicy.urlPatterns],
            })),
        })),
        rpaModules: [...config.rpaModules],
    };
}

function getCardPointeOrganizationIpgPortalUrl(
    config: CardPointeIpgConfig
): string {
    return config.allowedWebsites[0]?.url ?? '';
}

function getProductionCardPointeOrganizationIpgConfig(): CardPointeIpgConfig {
    const config = defaultIpgConfigByKey[CardPointeIpgSystemIdKey];
    if (config.ipg_id !== CardPointeIpgSystemId) {
        throw new Error('Invalid CardPointe production IPG configuration.');
    }
    return config;
}

function getDemoCardPointeOrganizationIpgConfig(): CardPointeIpgConfig {
    const demoIpgConfig = defaultDemoIpgConfigByKey[CardPointeIpgSystemIdKey];
    if (
        demoIpgConfig === undefined ||
        demoIpgConfig.ipg_id !== CardPointeIpgSystemId
    ) {
        throw new Error('Missing CardPointe demo IPG configuration.');
    }
    return demoIpgConfig;
}

function getEffectiveCardPointeOrganizationIpgConfig(
    merchantConfig: MerchantConfig | undefined
): CardPointeIpgConfig {
    const persistedCardPointeConfig =
        merchantConfig?.ipg[CardPointeIpgSystemIdKey];
    if (persistedCardPointeConfig?.ipg_id === CardPointeIpgSystemId) {
        return cloneCardPointeOrganizationIpgConfig(persistedCardPointeConfig);
    }
    return cloneCardPointeOrganizationIpgConfig(
        getProductionCardPointeOrganizationIpgConfig()
    );
}

function deriveCardPointeOrganizationIpgEnvironment(
    config: CardPointeIpgConfig
): CardPointeOrganizationIpgEnvironment {
    const portalUrl = getCardPointeOrganizationIpgPortalUrl(config);
    const productionPortalUrl = getCardPointeOrganizationIpgPortalUrl(
        getProductionCardPointeOrganizationIpgConfig()
    );
    if (
        config.environment === CardPointeGatewayEnvironment.PRODUCTION &&
        portalUrl === productionPortalUrl
    ) {
        return 'PRODUCTION';
    }
    const demoPortalUrl = getCardPointeOrganizationIpgPortalUrl(
        getDemoCardPointeOrganizationIpgConfig()
    );
    if (
        config.environment === CardPointeGatewayEnvironment.SANDBOX &&
        portalUrl === demoPortalUrl
    ) {
        return 'DEMO';
    }
    return 'CUSTOM';
}

export function buildCardPointeOrganizationIpgConfigEditorState(
    merchantConfig: MerchantConfig | undefined
): CardPointeOrganizationIpgConfigEditorState {
    const effectiveConfig =
        getEffectiveCardPointeOrganizationIpgConfig(merchantConfig);
    const environment = deriveCardPointeOrganizationIpgEnvironment(effectiveConfig);
    return {
        ipgSystemId: CardPointeIpgSystemId,
        ipgSystemIdKey: CardPointeIpgSystemIdKey,
        environment,
        gatewayEnvironment: effectiveConfig.environment,
        portalUrl: getCardPointeOrganizationIpgPortalUrl(effectiveConfig),
    };
}

function formatCardPointeOrganizationIpgEnvironmentLabel(
    environment: CardPointeOrganizationIpgEnvironment
): string {
    if (environment === 'PRODUCTION') {
        return 'Production';
    }
    if (environment === 'DEMO') {
        return 'Demo';
    }
    return 'Custom';
}

function isValidUrl(value: string): boolean {
    try {
        new URL(value);
        return true;
    } catch {
        return false;
    }
}

export function buildCardPointeOrganizationIpgConfigForSaveOrThrow(
    editorState: CardPointeOrganizationIpgConfigEditorState
): CardPointeIpgConfig {
    const trimmedPortalUrl = editorState.portalUrl.trim();
    if (!isValidUrl(trimmedPortalUrl)) {
        throw new Error('CardPointe portal URL must be a valid URL.');
    }

    const portalOrigin = new URL(trimmedPortalUrl).origin;
    const productionConfig = cloneCardPointeOrganizationIpgConfig(
        getProductionCardPointeOrganizationIpgConfig()
    );
    const productionAllowedWebsite = productionConfig.allowedWebsites[0];
    if (productionAllowedWebsite === undefined) {
        throw new Error(
            'CardPointe production config is missing an allowed website.'
        );
    }

    return {
        ...productionConfig,
        environment: editorState.gatewayEnvironment,
        urlPattern: `${portalOrigin}/*`,
        allowedWebsites: [
            {
                ...productionAllowedWebsite,
                url: trimmedPortalUrl,
                allowedUrlPatterns: [`${portalOrigin}/*`],
            },
        ],
    };
}

function CardPointeOrganizationIpgEnvironmentRadio(props: {
    readonly label: string;
    readonly checked: boolean;
    readonly onChange: () => void;
}): ReactElement {
    return (
        <div className="flex items-center gap-2 text-sm">
            <span
                className="-m-[3px] inline-flex p-[3px]"
                onClick={(event) => {
                    if (event.target !== event.currentTarget) {
                        return;
                    }
                    props.onChange();
                }}
            >
                <input
                    type="radio"
                    aria-label={props.label}
                    name="cardpointe_ipg_environment"
                    checked={props.checked}
                    className="h-4 w-4 accent-blue-600"
                    onChange={props.onChange}
                />
            </span>
            <span>{props.label}</span>
        </div>
    );
}

function buildCardPointeOrganizationIpgPresetState(
    editorState: CardPointeOrganizationIpgConfigEditorState,
    environment: Exclude<CardPointeOrganizationIpgEnvironment, 'CUSTOM'>
): CardPointeOrganizationIpgConfigEditorState {
    const presetConfig =
        environment === 'DEMO'
            ? getDemoCardPointeOrganizationIpgConfig()
            : getProductionCardPointeOrganizationIpgConfig();
    return {
        ...editorState,
        environment,
        gatewayEnvironment: presetConfig.environment,
        portalUrl: getCardPointeOrganizationIpgPortalUrl(presetConfig),
    };
}

export function CardPointeOrganizationIpgConfigEditor({
    editorState,
    onChange,
    onPresetConfigChange,
    onPortalUrlChange,
}: CardPointeOrganizationIpgConfigEditorProps): ReactElement {
    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">CardPointe gateway configuration</p>
            <div className="space-y-2">
                <p className="text-sm font-medium">Environment</p>
                <div className="flex flex-wrap gap-4">
                    {(['PRODUCTION', 'DEMO', 'CUSTOM'] as const).map((environment) => (
                        <CardPointeOrganizationIpgEnvironmentRadio
                            key={environment}
                            label={formatCardPointeOrganizationIpgEnvironmentLabel(
                                environment
                            )}
                            checked={editorState.environment === environment}
                            onChange={() => {
                                if (environment === 'CUSTOM') {
                                    onChange({
                                        ...editorState,
                                        environment,
                                    });
                                    return;
                                }
                                const nextState =
                                    buildCardPointeOrganizationIpgPresetState(
                                        editorState,
                                        environment
                                    );
                                onChange(nextState);
                                onPresetConfigChange(
                                    buildCardPointeOrganizationIpgConfigForSaveOrThrow(
                                        nextState
                                    )
                                );
                            }}
                        />
                    ))}
                </div>
            </div>
            <div className="space-y-2">
                <Label htmlFor="cardpointe_ipg_portal_url">Portal URL</Label>
                <Input
                    id="cardpointe_ipg_portal_url"
                    value={editorState.portalUrl}
                    onChange={(event) => {
                        onPortalUrlChange(event.target.value);
                        onChange({
                            ...editorState,
                            environment: 'CUSTOM',
                            portalUrl: event.target.value,
                        });
                    }}
                />
            </div>
        </div>
    );
}

export function CardPointeOrganizationIpgReadOnlySummary({
    editorState,
}: CardPointeOrganizationIpgReadOnlySummaryProps): ReactElement {
    return (
        <div className="space-y-2">
            <p className="text-sm font-medium">CardPointe gateway configuration</p>
            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <p>
                    Environment:{' '}
                    {formatCardPointeOrganizationIpgEnvironmentLabel(
                        editorState.environment
                    )}
                </p>
                <p>Portal URL: {editorState.portalUrl}</p>
                {CardPointeOrganizationIpgCapabilityDisplayRules.map((rule) => {
                    const isSupported =
                        rule.state === IpgPaymentFeatureCapabilityState.SUPPORTED;
                    return (
                        <p key={rule.featureId}>
                            {rule.label}:{' '}
                            <span
                                title={rule.tooltip}
                                aria-disabled={isSupported ? undefined : true}
                                className={isSupported ? undefined : 'opacity-50'}
                            >
                                {formatIpgPaymentFeatureCapabilityStateLabel(
                                    rule.state
                                )}
                            </span>
                        </p>
                    );
                })}
            </div>
        </div>
    );
}

function getCardPointeTerminalDeviceDisplayLabel(
    terminalDevice: TerminalDeviceListItem
): string {
    const configuredName = terminalDevice.name?.trim();
    return configuredName !== undefined && configuredName.length > 0
        ? configuredName
        : [
              terminalDevice.processor_name,
              terminalDevice.processor_mid,
              terminalDevice.processor_tid,
              terminalDevice.processor_device_id,
          ].join(' / ');
}

function CardPointeReadinessActionSummary(props: {
    readonly action: PaymentActionAvailability;
    readonly label: string;
}): ReactElement {
    const available =
        props.action.status === PaymentActionAvailabilityStatus.AVAILABLE;
    return (
        <div className="rounded border p-2" data-readiness-action={props.label}>
            <p className="text-sm font-medium">{props.label}</p>
            <p className="text-sm">{available ? 'Ready' : 'Needs attention'}</p>
            {!available && (
                <p className="text-sm text-muted-foreground">
                    {props.action.message}
                </p>
            )}
        </div>
    );
}

export function CardPointeOrganizationIpgReadinessSummary({
    organizationUuid,
}: CardPointeOrganizationIpgReadinessSummaryProps): ReactElement {
    const [selectedTerminalDevice, setSelectedTerminalDevice] =
        useState<TerminalDeviceListItem | null>(null);
    const selectedTerminalDeviceUuid =
        selectedTerminalDevice?.terminal_device_uuid ?? null;
    const readinessQuery = useQuery<PaymentActionReadinessResponse>({
        queryKey: [
            'payments',
            'cardpointeActionReadiness',
            organizationUuid,
            selectedTerminalDeviceUuid ?? 'none',
        ],
        queryFn: async () => {
            if (selectedTerminalDeviceUuid === null) {
                throw new Error(
                    'A terminal device must be selected for CardPointe readiness.'
                );
            }
            return await paymentsApi.getPaymentActionReadiness(
                selectedTerminalDeviceUuid
            );
        },
        enabled: selectedTerminalDeviceUuid !== null,
        retry: false,
    });

    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">CardPointe readiness</p>
            <OmnisearchSelect<TerminalDeviceListItem, string>
                label="Terminal device"
                placeholder="Search terminal devices..."
                pageSize={CARDPOINTE_READINESS_TERMINAL_PAGE_SIZE}
                resetToken={organizationUuid}
                overlayMenu={true}
                selectedItemId={selectedTerminalDeviceUuid}
                selectedItemLabel={
                    selectedTerminalDevice === null
                        ? undefined
                        : getCardPointeTerminalDeviceDisplayLabel(
                              selectedTerminalDevice
                          )
                }
                fetchPage={async (params) => {
                    const response =
                        await paymentsInfrastructureApi.searchTerminalDevices({
                            organization_uuid: organizationUuid,
                            include_descendants: false,
                            ...(params.terms !== undefined
                                ? { terms: [...params.terms] }
                                : {}),
                            limit: Math.min(
                                params.limit,
                                CARDPOINTE_READINESS_TERMINAL_PAGE_SIZE
                            ),
                            cursor: params.cursor,
                        });
                    return {
                        items: response.terminal_devices,
                        pagination: response.pagination,
                    };
                }}
                getItemId={(terminalDevice) =>
                    terminalDevice.terminal_device_uuid
                }
                getItemLabel={getCardPointeTerminalDeviceDisplayLabel}
                getItemFilterText={(terminalDevice) =>
                    [
                        getCardPointeTerminalDeviceDisplayLabel(terminalDevice),
                        terminalDevice.terminal_device_uuid,
                    ]
                        .join(' ')
                        .toLowerCase()
                }
                onSelect={setSelectedTerminalDevice}
                onClearSelection={() => setSelectedTerminalDevice(null)}
            />

            {selectedTerminalDeviceUuid === null ? (
                <p className="text-sm text-muted-foreground">
                    Select a terminal device to check CardPointe readiness.
                </p>
            ) : readinessQuery.isPending ? (
                <p className="text-sm" role="status">
                    Checking CardPointe readiness...
                </p>
            ) : readinessQuery.isError ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        <p>Unable to check CardPointe readiness.</p>
                        <Button
                            type="button"
                            variant="outline"
                            className="mt-2"
                            onClick={() => void readinessQuery.refetch()}
                        >
                            Retry readiness check
                        </Button>
                    </AlertDescription>
                </Alert>
            ) : (
                <div className="space-y-3">
                    {readinessQuery.isFetching && (
                        <p className="text-sm" role="status">
                            Refreshing CardPointe readiness...
                        </p>
                    )}
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {CardPointeReadinessActionDefinitions.map(
                            (definition) => (
                                <CardPointeReadinessActionSummary
                                    key={definition.key}
                                    label={definition.label}
                                    action={readinessQuery.data[definition.key]}
                                />
                            )
                        )}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={readinessQuery.isFetching}
                        onClick={() => void readinessQuery.refetch()}
                    >
                        Retry readiness check
                    </Button>
                </div>
            )}
        </div>
    );
}

export const CardPointeOrganizationIpgConfigEditorAdapter: IpgConfigEditorAdapter<
    CardPointeOrganizationIpgConfigEditorState,
    CardPointeIpgConfig
> = {
    ipgSystemId: CardPointeIpgSystemId,
    ipgSystemIdKey: CardPointeIpgSystemIdKey,
    label: 'CardPointe',
    capabilities: {
        supportsEditableConfig: true,
        supportsReadOnlySummary: true,
        supportsDefaultCredentialPlaceholders: true,
    },
    createEditableState: ({ merchantConfig }) =>
        buildCardPointeOrganizationIpgConfigEditorState(merchantConfig),
    validateEditableState: ({ editorState }) => {
        buildCardPointeOrganizationIpgConfigForSaveOrThrow(editorState);
        return [];
    },
    buildSavePayload: ({ editorState }) =>
        buildCardPointeOrganizationIpgConfigForSaveOrThrow(editorState),
    renderEditable: ({ editorState, onChange }) => (
        <CardPointeOrganizationIpgConfigEditor
            editorState={editorState}
            onChange={onChange}
            onPresetConfigChange={() => undefined}
            onPortalUrlChange={() => undefined}
        />
    ),
    renderReadOnlySummary: ({ merchantConfig, organizationUuid }) => (
        <div className="space-y-2">
            <CardPointeOrganizationIpgReadOnlySummary
                editorState={buildCardPointeOrganizationIpgConfigEditorState(
                    merchantConfig
                )}
            />
            <CardPointeOrganizationIpgReadinessSummary
                organizationUuid={organizationUuid}
            />
        </div>
    ),
};
